import { useState, useEffect, ChangeEvent } from "react";
import { Alert, Dialog, Select, TextInput, Button } from "@narmi/design_system";
import { Case } from "./CaseTable";
import { Payment } from "./PaymentTable";
import {
  CANCELLATION_CODES,
  CANCELLATION_CODES_TYPE,
  RETURN_ACCEPT_REASONS,
  RETURN_REJECT_REASONS,
} from "./codesets";
import { CSRF_TOKEN } from "@pages/csrf";

type ReturnFormData = {
  reason: string | undefined;
  additional_info: string | undefined;
  amount_cents: number | undefined;
};

function ReturnRequestDialog({
  caseobj,
  payment,
  action,
  setAction,
  successCallback,
}: {
  caseobj: Case | null;
  payment: Payment;
  action: "accept" | "reject" | "create" | null;
  setAction: CallableFunction;
  successCallback: CallableFunction;
}) {
  const [formData, setFormData]: [ReturnFormData, CallableFunction] = useState({
    reason: undefined,
    additional_info: undefined,
    amount_cents: undefined,
  });
  const [amountError, setAmountError]: [string | null, CallableFunction] = useState(null);
  const [error, setError]: [string | null, CallableFunction] = useState(null);
  const [disableSubmit, setDisableSubmit] = useState(false);

  useEffect(() => {
    if (payment && action === "accept") {
      const copy = { ...formData };
      copy.amount_cents = payment.amount_cents;
      setFormData(copy);
    }
  }, [payment, action]);

  async function onCreate() {
    setDisableSubmit(true);
    const path = `/v1/fednow/payments/${payment.payment_identification}/request_return`;
    const data = { ...formData };
    const res = await fetch(path, {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": CSRF_TOKEN,
      },
    });
    const resData = await res.json();
    if (!res.ok) {
      if (typeof resData.message === "string") {
        setError(resData.message);
      } else {
        setError(Object.values(resData)[0]);
      }
      setDisableSubmit(false);
      return;
    }
    successCallback(resData["case_id"]);
    setDisableSubmit(false);
    setAction(null);
  }

  async function onSubmit() {
    if (caseobj === null) {
      return;
    }
    setDisableSubmit(true);
    const path = `/v1/fednow/cases/${caseobj.case_identification}/return-${action}`;
    const data = { ...formData };
    if (action === "accept") {
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      (data as any)["resolution_reason"] = formData.reason;
    } else {
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      (data as any)["reject_reason"] = formData.reason;
    }
    const res = await fetch(path, {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": CSRF_TOKEN,
      },
    });
    const resData = await res.json();
    if (!res.ok) {
      if (typeof resData.message === "string") {
        setError(resData.message);
      } else {
        setError(Object.values(resData)[0]);
      }
      setDisableSubmit(false);
      return;
    }
    successCallback();
    setDisableSubmit(false);
    setAction(null);
  }

  const title =
    action === "reject"
      ? "Reject return request"
      : action === "create"
        ? "Request a return"
        : "Accept return request";
  return (
    <Dialog isOpen={action !== null} title={title} onUserDismiss={() => setAction(null)}>
      {error && (
        <div className="padding--bottom--s">
          <Alert isActive kind="error">
            {error}
          </Alert>
        </div>
      )}

      {action === "accept" && (
        <div className="padding--bottom--s">
          <TextInput
            label="Return amount"
            type="number" // ignore-danger-type-number
            step="any"
            inputMode="decimal"
            error={amountError}
            defaultValue={(payment.amount_cents / 100).toFixed(2)}
            startIcon="dollar-sign"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const copy = { ...formData };
              const num = Number(e.target.value);
              copy.amount_cents = num * 100;
              if (num <= 0 || num > payment.amount_cents / 100) {
                setAmountError(
                  "Invalid return amount, must be between zero and the original payment amount."
                );
              } else {
                setAmountError(null);
              }
              setFormData(copy);
            }}
          />
        </div>
      )}

      {action === "create" ? (
        <div className="padding--bottom--s">
          <Select
            label="Return reason"
            onChange={(val: string) => {
              const copy = { ...formData };
              copy.reason = val;
              setFormData(copy);
            }}
          >
            {Object.keys(CANCELLATION_CODES).map((r) => {
              return (
                <Select.Item value={r}>
                  {CANCELLATION_CODES[r as CANCELLATION_CODES_TYPE]}
                </Select.Item>
              );
            })}
          </Select>
        </div>
      ) : (
        <div className="padding--bottom--s">
          <Select
            label={action === "accept" ? "Resolution reason" : "Reject reason"}
            onChange={(val: string) => {
              const copy = { ...formData };
              copy.reason = val;
              setFormData(copy);
            }}
          >
            {Object.keys(action === "accept" ? RETURN_ACCEPT_REASONS : RETURN_REJECT_REASONS).map(
              (r) => {
                return (
                  <Select.Item value={r}>
                    {
                      (action === "accept" ? RETURN_ACCEPT_REASONS : RETURN_REJECT_REASONS)[
                        r as "NARR"
                      ]
                    }
                  </Select.Item>
                );
              }
            )}
          </Select>
        </div>
      )}

      <div className="padding--bottom--s">
        <TextInput
          label="Additional information"
          error={
            formData.reason === "NARR" &&
            (!formData.additional_info || formData.additional_info === "")
              ? "Additional information is required for narrative reason."
              : null
          }
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const copy = { ...formData };
            copy.additional_info = e.target.value;
            setFormData(copy);
          }}
        />
      </div>

      <div style={{ float: "right" }}>
        <Button
          label="Submit"
          onClick={() => {
            action === "create" ? onCreate() : onSubmit();
          }}
          disabled={disableSubmit || !formData.reason}
        />
      </div>
    </Dialog>
  );
}

export default ReturnRequestDialog;
